import React, { useEffect, useState } from 'react';

import { StepProps, useFlowState, useMutation } from '@betterment/js-runtime';
import { Box, Button } from '@betterment/samba-react';
import {
  CenteredWorkflowLayout,
  ErrorText,
  HR,
  Heading,
  ListRow,
  Text,
  WorkflowHeader,
} from '@betterment/style-closet-react';

import { locationAssign } from '../../Shared/path-helpers';
import { urlBuilder } from '../../Shared/urlBuilder';
import { FundAllocation, PortfolioFlowState } from '../data';
import { formattedAllocationsForSubmission } from '../models';

const PortfolioConfirmationStep: React.FC<React.PropsWithChildren<StepProps>> = () => {
  const {
    goToStep,
    flowState: {
      values: { goalId, fundReallocation, previousStep },
    },
  } = useFlowState<PortfolioFlowState>();

  const [isErrorOnSubmit, setErrorOnSubmit] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [createPortfolioAllocations, { loading: isSubmitting }] = useMutation<{}, { allocations: FundAllocation[] }>(
    urlBuilder.apiV2ExternallyAdvisedPlanAllocations.absolute({ goalId })
  );

  useEffect(() => {
    setIsLoading(isSubmitting);
  }, [isSubmitting]);

  if (!fundReallocation) {
    goToStep('select');
    return null;
  }

  const handleSubmit = async () => {
    const creationParams = {
      allocations: formattedAllocationsForSubmission(fundReallocation.fundAllocations || {}),
    };
    const response = await createPortfolioAllocations(creationParams);
    if (response.errors) {
      setErrorOnSubmit(true);
    } else {
      goToStep('success');
    }
  };

  return (
    <CenteredWorkflowLayout variant="six-column">
      <CenteredWorkflowLayout.Slot name="header">
        <WorkflowHeader
          title=""
          backButtonHandler={() => goToStep(previousStep as string, { fundReallocation })}
          closeHandler={() => locationAssign(urlBuilder.getGoalSettings.absolute({ goalId }))}
        />
      </CenteredWorkflowLayout.Slot>
      <CenteredWorkflowLayout.Slot name="content">
        <Heading as="h1" mb="11">
          Please confirm your portfolio
        </Heading>
        <Box>
          <Text as="h3">Change</Text>
          <HR />
          <Box role="row" aria-label="From Fund Allocation">
            <ListRow
              spacingX="tight"
              textAlignment="flush"
              leadingTitle="From"
              trailingTitle={fundReallocation?.fromName}
              trailingSubtitle={fundReallocation?.fromSubname ?? ''}
            />
          </Box>
          <HR />
          <Box role="row" aria-label="To Fund Allocation">
            <ListRow
              spacingX="tight"
              textAlignment="flush"
              leadingTitle="To"
              trailingTitle={fundReallocation?.toName}
              trailingSubtitle={fundReallocation?.toSubname ?? ''}
            />
          </Box>
          <HR />
        </Box>
        <Button
          variant="solidPrimary"
          loading={isLoading}
          css={{ mt: 'space.400', px: 'space.400' }}
          onClick={handleSubmit}
        >
          Confirm
        </Button>
        {isErrorOnSubmit && (
          <Box
            css={{
              paddingTop: 'space.100',
            }}
          >
            <ErrorText>Error submitting changes. Please try again later.</ErrorText>
          </Box>
        )}
      </CenteredWorkflowLayout.Slot>
    </CenteredWorkflowLayout>
  );
};

export default PortfolioConfirmationStep;
